import * as _ from 'lodash-es';
import Quill, { Delta } from 'quill/core';
import Header from './formats/Header';
import HeaderIdFormat, { generateHeaderId } from './formats/HeaderId';

const Module = Quill.import('core/module');

class HeaderId extends Module {
  static register() {
    Quill.register(HeaderIdFormat, true);
  }

  private debouncedCleanIds: () => void;
  private pendingUpdatedHeaderIds = new Set();

  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  constructor(quill: Quill, options: Record<string, any>) {
    super(quill, options);
    this.debouncedCleanIds = _.debounce(this.cleanIds.bind(this), 500);

    this.setupListener();
  }

  cleanIds() {
    const excludeSelectors = Header.tagName
      .map((tagName) => `:not(${tagName})`)
      .join('');
    const selector = `[${HeaderIdFormat.attrName}]${excludeSelectors}`;
    this.quill.root.querySelectorAll(selector).forEach((node) => {
      if (node instanceof HTMLElement) {
        HeaderIdFormat.remove(node);
      }
    });
  }

  getChangedHeaders(delta: Delta): Header[] {
    const { quill } = this;
    const changedHeaderIndexes: number[] = [];
    delta.reduce((index, op) => {
      if (op.delete) return index;
      const length = Delta.Op.length(op);
      const attributes = op.attributes || {};

      const newHeaderId = attributes[HeaderIdFormat.attrName];
      if (newHeaderId && this.pendingUpdatedHeaderIds.has(newHeaderId)) {
        this.pendingUpdatedHeaderIds.delete(newHeaderId);
      } else if (
        attributes[Header.blotName] ||
        HeaderIdFormat.attrName in attributes
      ) {
        changedHeaderIndexes.push(index);
      }
      return index + length;
    }, 0);

    return _.filter(
      changedHeaderIndexes.map((index) => quill.getLine(index)[0]),
      (line) => line instanceof Header,
    );
  }

  setupListener() {
    const { quill } = this;
    quill.on(Quill.events.TEXT_CHANGE, (delta, _old, source) => {
      if (source !== Quill.sources.USER) return;
      this.debouncedCleanIds();

      const changedHeaders = new Set(this.getChangedHeaders(delta));
      if (changedHeaders.size) {
        // Defer DOM operations until next tick to avoid other listeners of
        // TEXT_CHANGE from getting changes in different order (that changes
        // generated by this listener shouldn't be received by other listeners
        // before `delta`).
        _.defer(() => {
          // Sort headers to try to avoid updating headerId of unchanged headers.
          const headers = _.sortBy(
            quill.scroll.descendants(Header),
            (header) => (changedHeaders.has(header) ? 1 : -1),
          );

          const ids = new Set();
          headers.forEach((header) => {
            const headerId = HeaderIdFormat.value(header.domNode);
            if (!headerId || ids.has(headerId)) {
              const newHeaderId = generateHeaderId();
              this.pendingUpdatedHeaderIds.add(newHeaderId);
              // @ts-expect-error
              quill.history.squash(() => {
                header.format(HeaderIdFormat.attrName, newHeaderId);
                quill.update();
              });
              ids.add(newHeaderId);
            } else {
              ids.add(headerId);
            }
          });
        });
      }
    });
  }
}

export default HeaderId;
